/**
 * Profiles' Equipment tab — profiles, the preset library, and the volume
 * waterfall (PLAN §5.7: *"Displayed as a visual waterfall in the equipment
 * panel"*). A page of its own until P3; the name is the one every caller and
 * test already uses.
 *
 * ## Why the waterfall is the point
 *
 * An equipment profile is nineteen numbers, most of which mean nothing on their
 * own. "Kettle deadspace 0.4 gal" is not information; *"and that is why 6.4 gal
 * of water becomes 5 gal of beer"* is. So the editor puts the fields on the left
 * and a live waterfall on the right, recomputed through core's real `planVolumes`
 * on every keystroke — the same backwards pass the recipe editor runs, so the
 * preview cannot disagree with the thing it is previewing.
 *
 * The preview needs a brew to be about: losses are per-kilogram and per-hour, so
 * a profile alone has no volume. The page pins one (5 kg of grain per 5 gal,
 * 60 g of kettle hops, a 60 minute boil), scales the grist with the batch, and
 * says so on screen rather than pretending the numbers are universal.
 *
 * ## Built-ins and presets
 *
 * The three built-in profiles are read-only for the same determinism reason the
 * water built-ins are; "Duplicate to edit" is the escape hatch. The catalog
 * presets are a *different* thing — templates for real systems, copied once
 * into a profile the brewer then owns and recalibrates.
 *
 * ## And where the recalibrating happens
 *
 * "Recalibrates" used to mean "retypes". Each profile row now carries
 * {@link MeasuredFigures}: the median of what this rig actually measured over
 * every batch brewed on it, beside what the profile claims, with a button per
 * figure to adopt it. See that module for why it belongs on this page rather
 * than only on the batch that happened to reveal the drift.
 */

import type { EquipmentPreset } from '@beerwright/catalog';
import { equipmentPresets, normalize } from '@beerwright/catalog';
import type { EquipmentProfileDoc } from '@beerwright/commands';
import { CopyIcon, PencilIcon, PlusIcon, StarIcon, Trash2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import {
  BrewRemindersField,
  parseReminderMinutes,
  reminderMinMinutes,
  reminderMinutesText,
} from '@/components/equipment/brew-reminders-field';
import { MeasuredFigures } from '@/components/equipment/measured-figures';
import { VolumeWaterfallChart } from '@/components/equipment/volume-waterfall';
import { NumberField } from '@/components/number-field';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'; // prettier-ignore
import { Textarea } from '@/components/ui/textarea';
import type { EquipmentFieldKey, EquipmentFieldKind } from '@/lib/equipment';
import {
  DEFAULT_PREVIEW_BATCH_L,
  EQUIPMENT_FIELD_GROUPS,
  EQUIPMENT_FIELD_HINTS,
  nominalGrainKg,
  presetToEquipmentDoc,
} from '@/lib/equipment';
import { formatNumber } from '@/lib/format';
import { newId } from '@/lib/ids';
import { userContent } from '@/lib/posthog';
import type { ParseResult, RatioKind } from '@/lib/units';
import {
  formatMashThickness,
  formatQuantity,
  formatQuantityWithUnit,
  formatRatio,
  parseQuantity,
  parseRatio,
  ratioUnitLabel,
  unitLabel,
} from '@/lib/units';
import { useDispatch } from '@/store/use-dispatch';
import { useEquipmentProfiles, useSettings, useUnits } from '@/store/workspace-context';

/** Split a string into the tokens {@link presetMatchesQuery} compares. */
function tokensOf(value: string): readonly string[] {
  const normalized = normalize(value);
  return normalized.length === 0 ? [] : normalized.split(' ');
}

/**
 * True when every query token prefixes some token in the preset's name, maker,
 * description, tags, or sparge type. Same matching as the recipe list: case-
 * and accent-insensitive, AND across tokens, empty query matches everything.
 */
function presetMatchesQuery(preset: EquipmentPreset, query: string): boolean {
  const queryTokens = tokensOf(query);
  if (queryTokens.length === 0) return true;
  const haystack = [
    preset.name,
    preset.maker,
    preset.description,
    preset.spargeType,
    ...preset.tags,
  ].flatMap(tokensOf);
  return queryTokens.every((token) => haystack.some((part) => part.startsWith(token)));
}

/**
 * Group the catalog presets by maker, preserving catalog order so Grainfather
 * still leads and Generic still closes. A flat list of twenty-plus systems is
 * unreadable; the headings are the same `maker` field the badge already shows.
 */
function presetsByMaker(presets: readonly EquipmentPreset[]): readonly {
  readonly maker: string;
  readonly presets: readonly EquipmentPreset[];
}[] {
  const groups: { maker: string; presets: EquipmentPreset[] }[] = [];
  for (const preset of presets) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.maker === preset.maker) {
      last.presets.push(preset);
    } else {
      groups.push({ maker: preset.maker, presets: [preset] });
    }
  }
  return groups;
}

/** Lautering strategies, in the order the select lists them. */
const SPARGE_OPTIONS = [
  { value: 'batch', label: 'Batch sparge' },
  { value: 'fly', label: 'Fly sparge' },
  { value: 'no-sparge', label: 'No sparge' },
  { value: 'biab-full-volume', label: 'BIAB, full volume' },
] as const;

export function EquipmentPage() {
  const profiles = useEquipmentProfiles();
  const { dispatch } = useDispatch();
  const defaultProfileId = useSettings().defaultEquipmentProfileId;
  const [editing, setEditing] = useState<EquipmentProfileDoc | null>(null);
  const [deleting, setDeleting] = useState<EquipmentProfileDoc | null>(null);
  const [browsing, setBrowsing] = useState(false);

  const createFromPreset = (preset: EquipmentPreset): void => {
    const doc = presetToEquipmentDoc(preset, { id: newId('eq'), timestamp: 0 });
    // `equipment.create` takes the whole document as `from` and replaces the id,
    // the `builtin` flag and both timestamps from the envelope — so creating a
    // profile from a preset is one command and therefore one undo.
    if (dispatch('equipment.create', { profileId: doc.id, name: doc.name, from: doc })) {
      setBrowsing(false);
      setEditing(doc);
    }
  };

  const duplicate = (profile: EquipmentProfileDoc): void => {
    const profileId = newId('eq');
    const name = `${profile.name} (copy)`.slice(0, 120);
    if (dispatch('equipment.create', { profileId, name, from: profile })) {
      setEditing({ ...profile, id: profileId, name, builtin: false });
    }
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* A tab body, not a page (PLAN-UX-PASS F2). This component was the
          standalone `/equipment` page until P3 folded it into Profiles, and it
          kept the whole frame: a centred `max-w-5xl` column inside a page that
          is already padded, an `<h1>` under the page's own, and a description
          that said the tab's note over again. So half the tabs shifted sideways
          as you switched between them. The one fact the description carried and
          the note did not moved into the note. */}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" onClick={() => setBrowsing(true)}>
          <PlusIcon />
          New from preset
        </Button>
      </div>

      <ul className="flex flex-col gap-2">
        {profiles.map((profile) => (
          <ProfileRow
            key={profile.id}
            profile={profile}
            isDefault={profile.id === defaultProfileId}
            onMakeDefault={() =>
              dispatch('settings.update', { defaultEquipmentProfileId: profile.id })
            }
            onEdit={() => setEditing(profile)}
            onDuplicate={() => duplicate(profile)}
            onDelete={() => setDeleting(profile)}
          />
        ))}
      </ul>

      <PresetBrowser open={browsing} onOpenChange={setBrowsing} onPick={createFromPreset} />

      <Dialog
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      >
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-4xl">
          {editing !== null && (
            <ProfileEditor
              key={editing.id}
              initial={editing}
              onClose={() => setEditing(null)}
              onSave={(changes) => {
                if (dispatch('equipment.update', { profileId: editing.id, changes })) {
                  setEditing(null);
                }
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => {
          if (!open) setDeleting(null);
        }}
        title={`Delete "${deleting?.name ?? ''}"?`}
        description="Recipes using it fall back to the workspace default, and undo works until you reload."
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          if (deleting !== null) dispatch('equipment.delete', { profileId: deleting.id });
          setDeleting(null);
        }}
      />
    </div>
  );
}

/** One profile, with the numbers a brewer scans for. */
function ProfileRow({
  profile,
  isDefault,
  onMakeDefault,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  readonly profile: EquipmentProfileDoc;
  /** Whether a new recipe starts on this rig. */
  readonly isDefault: boolean;
  readonly onMakeDefault: () => void;
  readonly onEdit: () => void;
  readonly onDuplicate: () => void;
  readonly onDelete: () => void;
}) {
  const units = useUnits();
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-border bg-card/40 px-4 py-3">
      {/* Stacked on a phone: name-plus-badge and the buttons sharing one row
          leaves the name ~10 characters ("Generic 2.5 g…"), and the name is
          the only thing telling profiles apart. */}
      <div className="flex items-start justify-between gap-3 max-sm:flex-col max-sm:gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            {/* A built-in profile's name and notes are ours, out of the catalog. */}
            <h2
              className="truncate text-sm font-medium max-sm:whitespace-normal"
              {...(profile.builtin ? {} : userContent)}
            >
              {profile.name}
            </h2>
            {profile.builtin && <Badge variant="muted">Built-in</Badge>}
            {/* `settings.defaultEquipmentProfileId` has driven `recipe.create`
                since the beginning; nothing in the app could set it, so every
                brewer's new recipes started on whichever rig shipped first.
                A built-in can be the default like any other — it is a reference,
                and pointing at one changes nothing about it. */}
            {isDefault && <Badge variant="muted">Default for new recipes</Badge>}
          </div>
          {profile.notes !== '' && (
            <p
              className="max-w-2xl text-xs leading-snug whitespace-pre-line text-muted-foreground"
              {...(profile.builtin ? {} : userContent)}
            >
              {profile.notes}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!isDefault && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onMakeDefault}
            >
              <StarIcon />
              <span className="max-sm:hidden">Make default</span>
              <span className="sm:hidden">Default</span>
            </Button>
          )}
          {profile.builtin ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              aria-label="Duplicate to edit"
              onClick={onDuplicate}
            >
              <CopyIcon />
              <span className="sm:hidden">Duplicate</span>
              <span className="hidden sm:inline">Duplicate to edit</span>
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={onEdit}>
                <PencilIcon />
                Edit
              </Button>
              {/* The same escape hatch the built-ins get, for a different
                  reason: "my rig, but with the new kettle" starts from a copy,
                  not from a preset. */}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Duplicate ${profile.name}`}
                className="text-muted-foreground"
                onClick={onDuplicate}
              >
                <CopyIcon />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                aria-label={`Delete ${profile.name}`}
                className="text-muted-foreground"
                onClick={onDelete}
              >
                <Trash2Icon />
              </Button>
            </>
          )}
        </div>
      </div>

      <dl className="flex flex-wrap gap-x-5 gap-y-1 border-t border-border/60 pt-2 text-[11px]">
        <Fact
          label="Sparge"
          value={
            SPARGE_OPTIONS.find((o) => o.value === profile.spargeType)?.label ?? profile.spargeType
          }
        />
        <Fact
          label="Boil-off"
          value={formatQuantityWithUnit(profile.boilOffRateLPerHour, 'volumeRate', units, {
            decimals: 1,
          })}
        />
        <Fact
          label="Absorption"
          value={formatMashThickness(profile.grainAbsorptionLPerKg, units)}
        />
        <Fact label="Thickness" value={formatMashThickness(profile.mashThicknessLPerKg, units)} />
        <Fact label="Mash eff" value={`${formatNumber(profile.mashEfficiencyPercent, 0)} %`} />
        {profile.kettleVolumeL !== null && (
          <Fact
            label="Kettle"
            value={`${formatQuantity(profile.kettleVolumeL, 'volume', units)} ${unitLabel('volume', units)}`}
          />
        )}
      </dl>

      <MeasuredFigures profile={profile} />
    </li>
  );
}

function Fact({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="tracking-wide text-muted-foreground uppercase">{label}</dt>
      <dd className="font-mono tabular-nums">{value}</dd>
    </div>
  );
}

/** The catalog's preset library. */
function PresetBrowser({
  open,
  onOpenChange,
  onPick,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onPick: (preset: EquipmentPreset) => void;
}) {
  const units = useUnits();
  const [query, setQuery] = useState('');
  const groups = useMemo(
    () => presetsByMaker(equipmentPresets.filter((preset) => presetMatchesQuery(preset, query))),
    [query],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setQuery('');
        onOpenChange(next);
      }}
    >
      <DialogContent className="flex max-h-[85dvh] flex-col overflow-hidden max-sm:overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Start from a preset</DialogTitle>
          <DialogDescription>
            We use the maker's published capacities where they exist. Evaporation, deadspace and
            absorption are typical values for this kind of system, and we've marked them.
            Recalibrate boil-off and mash efficiency from your first few batches.
          </DialogDescription>
        </DialogHeader>
        <Input
          autoFocus
          aria-label="Search presets"
          placeholder="Search by name, maker, or type…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        {/*
          The list runs through the sheet's own bottom padding and carries the same amount
          itself, so the last preset scrolls to the sheet's edge instead of being clipped
          1.5rem above it — otherwise that padding reads as a blank strip between the list
          and the keyboard. The ingredient picker solves the same thing by taking `p-0`.
          The pull is split by breakpoint because the primitive pads by the safe-area
          expression below `sm` and by a flat `p-6` on the centred box above it.
        */}
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto max-sm:-mb-[max(1.5rem,env(safe-area-inset-bottom))] max-sm:pb-[max(1.5rem,env(safe-area-inset-bottom))] sm:-mb-6 sm:pb-6">
          {groups.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No preset matches that.
            </p>
          ) : (
            groups.map((group) => (
              <section key={group.maker} className="flex flex-col gap-2">
                <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                  {group.maker}
                </h3>
                <ul className="flex flex-col gap-2">
                  {group.presets.map((preset) => (
                    <li key={preset.id}>
                      <button
                        type="button"
                        onClick={() => onPick(preset)}
                        className="flex w-full flex-col gap-1 rounded-lg border border-border px-3 py-2.5 text-left transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                      >
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium">{preset.name}</span>
                          <Badge variant="outline">{preset.provenance}</Badge>
                          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                            {formatQuantity(preset.nominalBatchVolumeL, 'volume', units)}{' '}
                            {unitLabel('volume', units)} batch ·{' '}
                            {formatQuantity(preset.boilOffRateLPerHour, 'volumeRate', units)}{' '}
                            {unitLabel('volumeRate', units)} ·{' '}
                            {formatNumber(preset.mashEfficiencyPercent, 0)} % mash
                          </span>
                        </span>
                        <span className="text-xs leading-snug text-muted-foreground">
                          {preset.description}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** A field the brewer left empty or typed something unparseable into. */
interface FieldProblem {
  /** A numeric field, or a brew-day reminder row by position. */
  readonly key: EquipmentFieldKey | `brewReminders.${number}`;
  readonly label: string;
  readonly message: string;
}

/**
 * The form, with the live waterfall beside it.
 *
 * ## Why the numeric fields are held as text
 *
 * Nineteen numbers get retyped here, and retyping a number means first emptying
 * the box. A field whose `value` is formatted from the parsed number and whose
 * `onChange` drops text it cannot parse cannot be emptied: React repaints the
 * old number the instant the last digit goes, so `0.4` could only become `0.41`
 * or `0.44`, never `1.2`. The non-nullable fields were the worst — they refused
 * to be cleared at all.
 *
 * So the text is the state, anything can be typed, and the numbers come out of
 * it twice: on every keystroke that parses, to keep the waterfall live (that is
 * the page's whole point), and again on submit, where an empty non-nullable
 * field or unparseable text is a **validation error** rather than a keystroke
 * the form silently ate.
 */
function ProfileEditor({
  initial,
  onClose,
  onSave,
}: {
  readonly initial: EquipmentProfileDoc;
  readonly onClose: () => void;
  readonly onSave: (changes: Partial<EquipmentProfileDoc>) => void;
}) {
  const units = useUnits();
  const [draft, setDraft] = useState(initial);
  const [texts, setTexts] = useState(() => fieldTexts(initial, units));
  const [problems, setProblems] = useState<readonly FieldProblem[]>([]);
  // The reminder rows' typed minutes, keyed by reminder id, for the same reason
  // `texts` exists. A row nobody has typed into has no entry.
  const [reminderTexts, setReminderTexts] = useState<Readonly<Record<string, string>>>({});
  const [batchVolumeL, setBatchVolumeL] = useState(DEFAULT_PREVIEW_BATCH_L);
  const grainKg = useMemo(() => nominalGrainKg(batchVolumeL), [batchVolumeL]);

  /**
   * Re-read every field from its text.
   *
   * `draft` already carries the parsed value of every field that parses — the
   * keystroke handler put it there for the waterfall — so this only has to
   * decide whether the form is submittable, not recompute it.
   */
  const validate = (): readonly FieldProblem[] => {
    const found: FieldProblem[] = [];
    for (const group of EQUIPMENT_FIELD_GROUPS) {
      for (const field of group.fields) {
        const text = (texts[field.key] ?? '').trim();
        if (text === '') {
          if (!field.nullable) {
            found.push({ key: field.key, label: field.label, message: 'needs a number.' });
          }
          continue;
        }
        const parsed = parseField(text, field.kind, units);
        if (!parsed.ok) found.push({ key: field.key, label: field.label, message: parsed.error });
      }
    }
    draft.brewReminders.forEach((reminder, index) => {
      const key = reminderKey(index);
      const label = `Reminder ${index + 1}`;
      if (reminder.label.trim() === '') found.push({ key, label, message: 'needs a label.' });
      if (parseReminderMinutes(reminderMinutesText(reminder, reminderTexts), reminder) === null) {
        const min = reminderMinMinutes(reminder);
        found.push({ key, label, message: `needs a whole number of minutes, ${min} to 240.` });
      }
    });
    return found;
  };

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const found = validate();
        setProblems(found);
        if (found.length > 0) return;
        const { id, builtin, createdAt, updatedAt, ...changes } = draft;
        onSave(changes);
      }}
      className="flex flex-col gap-4"
    >
      <DialogHeader>
        <DialogTitle {...userContent}>{draft.name}</DialogTitle>
        <DialogDescription>
          Watch the waterfall on the right as you edit — every loss field moves exactly one step of
          it.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eq-name">Name</Label>
            <Input
              id="eq-name"
              value={draft.name}
              maxLength={120}
              required
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eq-sparge">Lautering</Label>
            <Select
              value={draft.spargeType}
              onValueChange={(value) =>
                setDraft({ ...draft, spargeType: value as EquipmentProfileDoc['spargeType'] })
              }
            >
              <SelectTrigger
                id="eq-sparge"
                aria-label="Lautering strategy"
                className="w-full sm:w-64"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SPARGE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {EQUIPMENT_FIELD_GROUPS.map((group) => (
            <fieldset key={group.title} className="flex flex-col gap-2">
              <legend className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
                {group.title}
              </legend>
              <p className="text-xs text-muted-foreground/80">{group.hint}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {group.fields.map((field) => (
                  <div key={field.key} className="flex flex-col gap-1.5">
                    <Label htmlFor={`eq-${field.key}`} className="text-xs">
                      {fieldLabel(field.label, field.kind, units)}
                    </Label>
                    <Input
                      id={`eq-${field.key}`}
                      type="text"
                      inputMode="decimal"
                      aria-label={fieldLabel(field.label, field.kind, units)}
                      aria-invalid={
                        problems.some((problem) => problem.key === field.key) || undefined
                      }
                      placeholder={field.nullable ? 'not set' : undefined}
                      className="h-8 font-mono text-sm tabular-nums"
                      value={texts[field.key]}
                      onChange={(event) => {
                        const typed = event.target.value;
                        setTexts((current) => ({ ...current, [field.key]: typed }));
                        // Typing is the fix for a complaint, so it retracts it.
                        setProblems((current) =>
                          current.filter((problem) => problem.key !== field.key),
                        );
                        const text = typed.trim();
                        if (text === '') {
                          if (field.nullable) setDraft({ ...draft, [field.key]: null });
                          return;
                        }
                        const parsed = parseField(text, field.kind, units);
                        if (!parsed.ok) return;
                        setDraft({ ...draft, [field.key]: parsed.value });
                      }}
                    />
                    {EQUIPMENT_FIELD_HINTS[field.key] !== undefined && (
                      <p className="text-[11px] leading-snug text-muted-foreground/80">
                        {EQUIPMENT_FIELD_HINTS[field.key]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          ))}

          <BrewRemindersField
            reminders={draft.brewReminders}
            minutesTexts={reminderTexts}
            problems={problems}
            onChange={(next) => {
              const previous = draft.brewReminders;
              // Typing is the fix for a complaint, so it retracts that row's.
              // Removing a row renumbers the ones after it, so every reminder
              // complaint goes rather than pointing at the wrong row; Save
              // re-reads them all anyway.
              const retract =
                next.length < previous.length
                  ? (key: FieldProblem['key']) => key.startsWith('brewReminders.')
                  : (key: FieldProblem['key']) =>
                      next.some(
                        (reminder, index) =>
                          reminder !== previous[index] && key === reminderKey(index),
                      );
              setProblems((current) => current.filter((problem) => !retract(problem.key)));
              setDraft({ ...draft, brewReminders: [...next] });
            }}
            onMinutesTextChange={(id, text) => {
              setReminderTexts((current) => ({ ...current, [id]: text }));
              const key = reminderKey(draft.brewReminders.findIndex((r) => r.id === id));
              setProblems((current) => current.filter((problem) => problem.key !== key));
            }}
          />

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="eq-notes">Notes</Label>
            <Textarea
              id="eq-notes"
              rows={3}
              value={draft.notes}
              onChange={(event) => setDraft({ ...draft, notes: event.target.value })}
            />
          </div>
        </div>

        <aside className="flex flex-col gap-3 self-start lg:sticky lg:top-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">
              Volume waterfall
            </h3>
            <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <span aria-hidden="true">Batch</span>
              {/* Purely local view state, so it redraws the chart on every
                  keystroke that parses — but it holds the typed text until the
                  field is left, rather than reformatting `40` into `10.57` from
                  under the caret while the brewer is still typing `40 L`. */}
              <NumberField
                label="Preview batch volume"
                className="h-7 w-20 font-mono text-xs tabular-nums"
                value={batchVolumeL}
                format={(value) => formatQuantity(value, 'volume', units)}
                parse={(text) => parseBatchVolume(text, units)}
                onType={setBatchVolumeL}
                onCommit={setBatchVolumeL}
              />
              <span aria-hidden="true">{unitLabel('volume', units)}</span>
            </div>
          </div>
          <VolumeWaterfallChart profile={draft} batchVolumeL={batchVolumeL} grainKg={grainKg} />
          <p className="text-[11px] leading-snug text-muted-foreground/80">
            Drawn against a nominal brew: {formatNumber(grainKg, 1)} kg of grain, 60 g of kettle
            hops, a 60 minute boil. Your recipes use their own grist — this is here so you can see
            what each field does.
          </p>
        </aside>
      </div>

      {problems.length > 0 && (
        <div role="alert" className="flex flex-col gap-0.5 text-xs text-destructive">
          {problems.map((problem) => (
            // A reminder row can be missing its label and its minutes at once.
            <p key={`${problem.key}:${problem.message}`}>
              {problem.label} {problem.message}
            </p>
          ))}
        </div>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit">Save profile</Button>
      </DialogFooter>
    </form>
  );
}

/** The problem key naming a brew-day reminder row. */
function reminderKey(index: number): `brewReminders.${number}` {
  return `brewReminders.${index}`;
}

/** The ratio kinds, told from the plain `MeasureKind`s a field can carry. */
function isRatioKind(kind: EquipmentFieldKind): kind is RatioKind {
  return (
    kind === 'volumePerGrainMass' || kind === 'volumePerHopMass' || kind === 'temperaturePerHour'
  );
}

/** What a numeric field shows: blank for `null`, otherwise the display value. */
function fieldText(
  value: number | null,
  kind: EquipmentFieldKind,
  units: Parameters<typeof formatQuantity>[2],
): string {
  if (value === null) return '';
  return isRatioKind(kind) ? formatRatio(value, kind, units) : formatQuantity(value, kind, units);
}

/** A typed field back to its canonical value, whichever kind it is. */
function parseField(
  text: string,
  kind: EquipmentFieldKind,
  units: Parameters<typeof formatQuantity>[2],
): ParseResult {
  return isRatioKind(kind) ? parseRatio(text, kind, units) : parseQuantity(text, kind, units);
}

/** Every numeric field of a profile as the text the form opens with. */
function fieldTexts(
  profile: EquipmentProfileDoc,
  units: Parameters<typeof formatQuantity>[2],
): Record<EquipmentFieldKey, string> {
  const texts = {} as Record<EquipmentFieldKey, string>;
  for (const group of EQUIPMENT_FIELD_GROUPS) {
    for (const field of group.fields) {
      texts[field.key] = fieldText(profile[field.key], field.kind, units);
    }
  }
  return texts;
}

/**
 * The preview's batch volume: a volume, and a positive one.
 *
 * Zero is rejected rather than clamped because a zero-volume brew makes every
 * bar of the waterfall meaningless, and the honest response to `0` is to leave
 * the chart drawn against the last real number.
 */
function parseBatchVolume(text: string, units: Parameters<typeof formatQuantity>[2]): ParseResult {
  const parsed = parseQuantity(text, 'volume', units);
  if (!parsed.ok) return parsed;
  if (parsed.value <= 0) return { ok: false, error: 'A batch has to have some volume in it.' };
  return parsed;
}

/** Field title plus the display unit, so "Boil-off" reads "Boil-off (gal/h)" in US. */
function fieldLabel(
  label: string,
  kind: EquipmentFieldKind,
  units: Parameters<typeof formatQuantity>[2],
): string {
  if (isRatioKind(kind)) return `${label} (${ratioUnitLabel(kind, units)})`;
  if (kind === 'volume' || kind === 'volumeRate' || kind === 'temperature') {
    return `${label} (${unitLabel(kind, units)})`;
  }
  return label;
}
